import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LeafTabViewHeader from "../../../common/leaf/LeafTabViews/LeafTabViewHeader/LeafTabViewHeader";
import { LeafToggleSwitch } from "../../../common/leaf/LeafToggleSwitch/LeafToggleSwitch";
import { IconCheck } from "../../../common/icons/IconCheck/IconCheck";
import { MonthlyPerformanceIncome } from "./MonthlyPerformanceIncome/MonthlyPerformanceIncome";
import { AnnualPerformanceIncome } from "./AnnualPerformanceIncome/AnnualPerformanceIncome";
import defaultContent from "../../../content/performanceIncomeDashboard";
import { useAppDispatch, useAppSelector } from "../../../store";
import { SELECTOR_TYPE, updateSelectorType } from "../../../reducers/period";
import { setWasClicked, toggleShowData } from "../../../reducers/showData";
import {
  trackPerformanceIncomeToggle,
  trackShowDataToggle,
  trackAnnualDashboardPageView,
  trackMonthlyDashboardPageView,
} from "../../../common/tealium/PerformanceIncomeDashboard/PerformanceIncomeDashboardTrack";
import "./PerformanceIncomeDashboard.scss";

const PERFORMANCE_TABS = {
  MONTHLY: "monthly",
  ANNUAL: "annual",
};

const getTabsForPersona = (personaV2: number) =>
  personaV2 >= 2 ? [PERFORMANCE_TABS.MONTHLY, PERFORMANCE_TABS.ANNUAL] : [PERFORMANCE_TABS.MONTHLY];

export const PerformanceIncomeDashboard = () => {
  const { t } = useTranslation(["performanceIncomeDashboard", "monthlyPerformanceTotalModals", "rubyBonusModal"]);
  const dispatch = useAppDispatch();
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { shellConfig, isShellFetching, configuration } = useAppSelector((state) => state.boot);
  const showData = useAppSelector((state) => state.showData.showData);
  const showDataToggleEnabled = configuration.landingPage?.showDataToggle ?? false;
  const wasShowDataClicked = useAppSelector((state) => state.showData.wasClicked);
  const selectedPeriod = useAppSelector((state) => state.period.selectedPeriod);
  const personaV2 = shellConfig.personaV2;

  // Initialize from the current shellConfig value so the first render already
  // knows about both tabs (avoids a flash to monthly-only).
  const [availableTabs, setAvailableTabs] = useState(() => getTabsForPersona(shellConfig.personaV2));

  // Only update availableTabs once shellConfig has finished fetching.
  // During a refetch, personaV2 may briefly reset — we skip those transient values.
  // Once the fetch completes, we apply the real value (which may shrink or grow tabs).
  useEffect(() => {
    if (!isShellFetching) {
      setAvailableTabs(getTabsForPersona(shellConfig.personaV2));
    }
  }, [shellConfig.personaV2, isShellFetching]);

  const hashTab = hash.replace("#", "");
  const isTabHash = hashTab === PERFORMANCE_TABS.MONTHLY || hashTab === PERFORMANCE_TABS.ANNUAL;
  const selectedTab = isTabHash && availableTabs.includes(hashTab) ? hashTab : PERFORMANCE_TABS.MONTHLY;

  // Set default hash if missing or invalid — only redirect for tab-related hashes
  useEffect(() => {
    if (isTabHash && !availableTabs.includes(hashTab)) {
      navigate(`${pathname}${search}#${PERFORMANCE_TABS.MONTHLY}`, { replace: true });
    }
  }, [availableTabs, hashTab, isTabHash, navigate, pathname, search]);

  // Update period selector type when tab changes
  useEffect(() => {
    const selectorType = selectedTab === PERFORMANCE_TABS.ANNUAL ? SELECTOR_TYPE.PY : SELECTOR_TYPE.MONTHLY;
    dispatch(updateSelectorType(selectorType));
  }, [selectedTab, dispatch]);

  // Track page view when tab or period changes
  useEffect(() => {
    if (selectedTab === PERFORMANCE_TABS.ANNUAL) {
      trackAnnualDashboardPageView(personaV2, selectedPeriod);
    } else {
      trackMonthlyDashboardPageView(personaV2, selectedPeriod);
    }
  }, [selectedTab, selectedPeriod, personaV2]);

  useEffect(() => {
    // If toggle button is disabled and showData is set to false then set showData to true
    if (!showData && !showDataToggleEnabled) {
      dispatch(toggleShowData());
    }
    // If toggle button was originally toggled and user switches back to page where toggle
    // button is enabled, and showData is set to true, then set showData to false
    if (wasShowDataClicked && showDataToggleEnabled && showData) {
      dispatch(toggleShowData());
    }
  }, [showData, showDataToggleEnabled, wasShowDataClicked, dispatch]);

  const handleTabClick = (tabId: string) => {
    trackPerformanceIncomeToggle(tabId);
    navigate(`${pathname}${search}#${tabId}`, { replace: true });
  };

  const handleTabKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const tabElements = event.currentTarget.querySelectorAll<HTMLElement>('[role="tab"]');
    const arr = Array.from(tabElements);
    const lastIndex = arr.length - 1;
    const index = arr.findIndex((el) => el.id === (event.target as HTMLElement).id);
    if (index === -1) return;
    if (event.key === "ArrowLeft") {
      const target = index === 0 ? arr[lastIndex] : arr[index - 1];
      target.focus();
      handleTabClick(target.id);
    } else if (event.key === "ArrowRight") {
      const target = index === lastIndex ? arr[0] : arr[index + 1];
      target.focus();
      handleTabClick(target.id);
    }
  };

  const tabItems = [
    {
      id: PERFORMANCE_TABS.MONTHLY,
      title: `${t("monthly", defaultContent.monthly as string)}`,
    },
    {
      id: PERFORMANCE_TABS.ANNUAL,
      title: `${t("annual", defaultContent.annual as string)}`,
    },
  ];

  return (
    <div className="PerformanceIncomeDashboard" data-testid="performance-income-dashboard">
      <div className="PerformanceIncomeDashboard__header">
        {availableTabs.length > 1 && (
          // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- keyboard navigation handler for tab switching
          <div onKeyDown={handleTabKeyDown}>
            <div className="PerformanceIncomeDashboard__tabs" role="tablist" data-testid="performance-tabs">
              {tabItems.map((tab) => (
                <LeafTabViewHeader
                  key={tab.id}
                  tabItem={{ ...tab, activeView: selectedTab }}
                  tabHeaderOnClick={handleTabClick}
                  buttonify={true}
                />
              ))}
            </div>
          </div>
        )}
        {showDataToggleEnabled && (
          <div className="PerformanceIncomeDashboard__toggle">
            <span className="PerformanceIncomeDashboard__toggle-label">{`${t("toggle", defaultContent["toggle"])}`}</span>
            <LeafToggleSwitch
              isToggled={showData}
              onToggle={() => {
                trackShowDataToggle(!showData);
                dispatch(toggleShowData());
                dispatch(setWasClicked());
              }}
              name="showData"
              toggleOnIcon={<IconCheck color="blue" />}
            />
          </div>
        )}
      </div>
      <div className="PerformanceIncomeDashboard__content">
        {selectedTab === PERFORMANCE_TABS.MONTHLY ? <MonthlyPerformanceIncome /> : <AnnualPerformanceIncome />}
      </div>
    </div>
  );
};
